import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { TaxonomyService } from 'components/sharpr/taxonomy/TaxonomyService';
import { UrlModel } from '../../../sharpr/url/model/UrlModel.js';
import { UrlParamsMapper } from 'components/sharpr/urlParamsMapper/UrlParamsMapper';
import _ from 'lodash';
import moment from 'moment';
import { ensure } from 'components/sharpr/ensure/ensure';
import { AuthTypes } from '../../../sharpr/auth/AuthTypes.js';

export function PostListingRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.PostListing', {
		url: '/posts',
		controllerAs: 'vm',
		controller: PostListingCtrl,
		templateUrl: '/ng/hub/Post/Listing/PostListingCtrl.html?' + window.BUILD,
		permission: ['login'],
		resolve: {
			$style: () => AssetManager.css('/ng/hub/Post/Listing/PostListingCtrl.css'),
			$title: (Cfg, __cfgPreferences) => {
				'ngInject';
				return Cfg.read('TabLabel.intel');
			},
			// In June 2020 we changed from numeric categories/classifications in the URL to category/classification names.
			// Some users have bookmarks that contain category/classification ids. In order to keep those
			// bookmarks working, we convert numeric category/classification ids in the URL before this
			// controller reads the URL
			convertNumericCategories: Api => {
				'ngInject';
				const match = window.location.href.match(/#(.+finetune=.+categories:)(!\([0-9,']+\))(.+)$/);
				if (!match) {
					return true;
				}
				const [, prefix, risonIds, postfix] = match;
				const ids = UrlModel.risonParse(risonIds);
				if (!Array.isArray(ids)) {
					return true;
				}
				const params = { ids: ids.join(',') };
				const headers = { Hubs: 'mine' };
				return Api.get('/categories/search', params, headers).then(
					cats => {
						if (cats.length === 0) {
							// user did not have access to any of those categories
							return true;
						}
						const names = cats.map(c => c.name);
						const risonNames = UrlModel.risonStringify(names);
						window.location.hash = prefix + risonNames + postfix;
						return true;
					},
					() => {}
				);
			},
			convertNumericClassifications: Api => {
				'ngInject';
				const match = window.location.href.match(/#(.+finetune=.+classifications:)(!\([0-9,']+\))(.+)$/);
				if (!match) {
					return true;
				}
				const [, prefix, risonIds, postfix] = match;
				const ids = UrlModel.risonParse(risonIds);
				if (!Array.isArray(ids)) {
					return true;
				}
				const params = { ids: ids.join(',') };
				const headers = { Hubs: 'mine' };
				return Api.get('/classifications/search', params, headers).then(
					classifications => {
						if (classifications.length === 0) {
							// user did not have access to any of those classifications
							return true;
						}
						const names = classifications.map(c => c.name);
						const risonNames = UrlModel.risonStringify(names);
						window.location.hash = prefix + risonNames + postfix;
						return true;
					},
					() => {}
				);
			},
			// for categories bar
			categories: TaxonomyService.resourcifyCategories(),
			clientUserCount: TaxonomyService.resourcifyClientUserCount(),
			embeds: ApiResourcify.endpoint('/dashboard_embeds'),
		},
		onExit: FileDrag => {
			'ngInject';
			FileDrag.cleanup();
		},
	});
}

let wasIframeLogged = false;

export class PostListingCtrl {
	constructor(
		$scope,
		$timeout,
		$window,
		Cfg,
		Api,
		Post,
		modal,
		categories,
		clientUserCount,
		Identity,
		EventTracker,
		FileDrag,
		FineTunePosts,
		UrlData,
		Logger,
		embeds,
		storage,
		Theme,
		Taxonomy
	) {
		'ngInject';
		// ensure we are not recruiter
		if (Cfg.read('App.solution') == 'recruiter') {
			window.location.href = '/posts/intelligence';
			return;
		}
		// log (once) if user is in iframe
		if (!wasIframeLogged) {
			const client = Identity.currentClient();
			if (window !== window.parent) {
				Logger.write('INTEL_IFRAME', client ? client.id : 0);
			}
			wasIframeLogged = true;
		}

		// Find whether "finetune" has been set in the search criteria and capture it.
		try {
			this.finetune = ensure.object(UrlData.get('finetune'));
		} catch (e) {
			// The "finetune" was not found in the search criteria, so we let it remain empty.
			this.finetune = {};
		}

		// redirect to new insights page unless this hub is explicitly set to continue to use the classic page
		// I recognize this piece is really gnarly and the plan is to quickly remove the classic insights page
		if (!Cfg.readAsBool('UI.use_classic_insights_page')) {
			const preferredHomePage = Cfg.read('Features.home_page');
			const destination = `${window.BASE_URL}${preferredHomePage}`;
			const reactQueryString = UrlParamsMapper.mapAngularSearchObjToReactQueryParams(this.finetune);
			let urlToRedirectTo = '';
			if (reactQueryString) {
				urlToRedirectTo = `${destination}?${reactQueryString}`;
			} else {
				urlToRedirectTo = destination;
			}
			window.location.href = urlToRedirectTo;
		}

		// save injections
		this.$timeout = $timeout;
		this.Api = Api;
		this.Post = Post;
		this.modal = modal;
		this.Identity = Identity;
		this.EventTracker = EventTracker;
		this.FileDrag = FileDrag;
		this.FineTunePosts = FineTunePosts;
		this.Cfg = Cfg;
		this.embeds = embeds;
		this.Theme = Theme;
		this.Taxonomy = Taxonomy;
		this.$scope = $scope;

		// setup
		if (this.Cfg.read('Ui.intel_layout_version') > 1) {
			this.lstoreLayout = 'last_intel_layout_v' + this.Cfg.read('Ui.intel_layout_version');
		} else {
			this.lstoreLayout = 'last_intel_layout';
		}
		this.title = this.Cfg.read('TabLabel.intel') || 'Insights';
		this.subtitle = this.Cfg.read('TabSubtitle.intel') || 'Search all of your curated posts';
		this.showTopContributors = this.Cfg.read('Features.top_contributors') === '1';
		this.hideLikesAndComments = this.Cfg.read('Features.hide_likes_and_comments') === '1';
		this.enableFileDrag = this.Identity.can('create_post_link') && this.Cfg.read('UI.enable_file_drag_and_drop') === '1';
		this.posts = [];
		this.myFavoritesPosts = [];
		this.hasUsedDashboard = false;
		this.ecosystemSources = [];
		// updated when hero slider needs to reset
		this.ecosystemSlideReload = 0;
		this.insightsFavoritesSlideReload = 0;
		this.ecosystemPostsPage = 0;
		this.ecosystemPosts = [];
		// ecosystem only enabled if the hub has sources set up
		// this value is checked in `loadEcosystemPosts`
		this.hasEcosystem = undefined;
		this.ecosystemJwt = undefined;
		this.configureEnabledLayouts();
		if (this.Cfg.read('Ui.intel_layout_force')) {
			this.layout = this.Cfg.read('Ui.intel_layout_force');
			this.setLayout(this.layout);
		} else {
			this.layout = this.getLastLayout();
			this.setLayout(this.layout);
		}
		this.loadFineTuneMessageData();
		UrlData.bindAll($scope, { layout: this.layout });
		this.status = 'loading';
		this.categories = categories;
		this.hasClientUsers = clientUserCount > 0;
		this.canStandardIntel = Identity.can('standard_intel');
		this.hasTrendsLink = Identity.can('manage_users') && this.Cfg.read('Features.trends_link') == 1;
		this.fineTunerClosed = true;
		this.clientToggleTooltips = [
			'Click to filter to posts only visible to clients',
			'These posts are visible to clients. Click to view all posts',
		];
		this.sortOptions = {
			score: 'Most Relevant',
			publish_date: 'Newest',
			modified: 'Recently Edited',
			score_date_weighted: 'Most Relevant and Recent',
		};
		this.sortIsOpen = false;
		try {
			const orderByUrl = this.finetune.orderBy || '';
			const orderByDefault =
				window.sessionStorage.getItem('Search.default_sort') ||
				this.Cfg.read('Search.default_sort') ||
				this.sortOptions.score;
			this.sortLabel = orderByUrl ? this.sortOptions[orderByUrl] : this.sortOptions[orderByDefault];
		} catch (e) {
			this.sortLabel = this.sortOptions.score;
		}

		// initialize
		if (this.enableFileDrag) {
			this.FileDrag.setup();
			this.FileDrag.setController(this);
		}
		this.showDupesModal = false;
		this.filesWithDuplicates = [];
		this.Taxonomy.getPublishers().then(p => (this.publishers = p));
		this.suggest = this.suggest.bind(this);
		this.chooseSelected = this.chooseSelected.bind(this);
		this.searchTerm = this.finetune.term || '';
		this.search = {
			clientIntel: false,
			term: '',
			orderBy: '',
			myFavorites: false,
			multihub: '',
		};
		this.fineTuneFiltersUpdated = this.fineTuneFiltersUpdated.bind(this);
		this.runEcosystemOnSearch();
		this.fetchFirstFavoritesPage();
		// update the available search items like categories and classifications if switching to multihub
		$scope.$watch('vm.search.multihub', this.loadFineTuneMessageData.bind(this));
	}

	suggest(term) {
		if (!this.Cfg.readAsBool('Features.search_autosuggest')) {
			// autosuggest is disabled
			return [];
		}
		if (this.lastSuggestRequest) {
			this.lastSuggestRequest.abort();
		}
		if (term.length < 2) {
			// need at least 2 characters before suggestions
			return [];
		}
		// start off with suggested publishers
		const suggestions = this.getPublisherSuggestions(term);
		// then fetch suggested search phrases
		const params = { term, limit: 10 };
		const headers = { avoidLoadingBar: true };
		if (this.FineTunePosts.hubs) {
			headers.Hubs = this.FineTunePosts.hubs;
		}
		const request = this.Api.get('/v3/posts/search/suggest', params, headers);
		this.lastSuggestRequest = request;
		return request.then(phrases => {
			if (!Array.isArray(phrases)) {
				// possibly aborted
				return false;
			}
			// format each phrase and associate a click handler
			phrases.forEach(phrase => {
				suggestions.push({
					term: phrase,
					label: phrase, // TODO: add bold or highlighting html to phrase
					type: 'term',
					onClick: this.chooseSelected,
				});
			});
			return suggestions;
		});
	}

	getPublisherSuggestions(term) {
		if (!this.publishers) {
			return [];
		}
		term = term.toLowerCase();
		const suggestions = [];
		this.publishers.forEach(p => {
			if (p.$search.indexOf(term) === -1) {
				return;
			}
			// format the publisher as a suggestion
			suggestions.push({
				term: p.email,
				label: p.fullName, // TODO: add bold or highlighting html to name
				type: 'contributor',
				onClick: this.chooseSelected,
			});
		});
		return suggestions;
	}

	chooseSelected(sugg, device) {
		this.$timeout(() => {
			this.searchTerm = sugg.term;
			setTimeout(() => {
				document.querySelector('sh-search-bar [type=submit]').click();
			}, 15);
		});
		this.EventTracker.save('search_suggestion_click', {
			page: 'insights',
			type: sugg.type,
			term: sugg.term,
			label: sugg.label,
			device,
		});
	}

	getLastLayout() {
		const lastLayout = window.localStorage.getItem(this.lstoreLayout);
		if (lastLayout === 'dashboard' || !this.enabledLayouts[lastLayout]) {
			return this.Cfg.read('Ui.intel_layout_default');
		} else {
			return lastLayout || this.Cfg.read('Ui.intel_layout_default');
		}
	}

	setLayout(newLayout) {
		if (newLayout === 'dashboard') {
			this.lastPostLayout = this.layout;
			this.hasUsedDashboard = true;
		} else if (this.enabledLayouts[newLayout]) {
			window.localStorage.setItem(this.lstoreLayout, newLayout);
		}

		this.layout = newLayout;

		if (newLayout === 'hero' && this.ecosystemPosts.length === 0) {
			this.loadEcosystemPosts();
		}
	}

	createPost() {
		this.Post.create().then(
			postId => {
				if (!Number(postId)) {
					// user canceled
					return;
				}
				this.EventTracker.save('create_post', {
					post_id: postId,
				});
				if (!this.Identity.can('submit_to_intel_tab')) {
					this.modal.alert({ text: 'Success! An editor will review and publish your submission.' });
					return;
				}
				this.Api.get(`/v3/posts/${postId}`).then(post => {
					if (post) {
						this.spliceInNewPost(post);
					}
				});
			},
			() => {}
		);
	}

	spliceInNewPost(post) {
		// find index of first non-pinned post
		const idx = _.findIndex(this.posts, { is_pinned: false });
		if (idx == -1) {
			// no posts or all posts are pinned
			this.posts.push(post);
		} else {
			// add before first non-pinned post
			this.posts.splice(idx, 0, post);
		}
	}

	toggleClientFilter() {
		this.search.clientIntel = !this.search.clientIntel;
	}

	/*
	 * Adds duplicates to `this.filesWithDuplicates`, which will
	 * show them to the user so they can decide to upload dupe or cancel.
	 * @param file Object  plupload file object with other attributes added
	 * 					   with some extra attributes
	 */
	checkDupesBeforeCreate(file) {
		if (file.post && file.post.fileError) {
			// don't show modal if the file didn't upload
			return;
		}
		if (file.duplicates) {
			// shows modal
			this.filesWithDuplicates.push(file);
		} else {
			this.createUploadedPost(file);
		}
	}

	/*
	 * Uploads the file, ignoring duplicates
	 * @param file Object  plupload file object with other attributes added
	 * 					   with some extra attributes
	 */
	uploadDuplicate(uploadedFileIdx, file) {
		// remove the file from the modal
		this.filesWithDuplicates.splice(uploadedFileIdx, 1);
		const params = {
			cloud_file_bucket: file.cloud_file_bucket,
			cloud_file_key: file.s3Name,
			name: file.name,
			size: file.size,
			sha256: file.sha256,
			allow_duplicates: true,
			folders: ensure.array(file.dirs),
			transcribe: file.transcribe || false,
		};
		return this.Api.post(`/attachments`, params).then(report => {
			// if there are duplicates the first time a request is sent to /attachments, no attachments record
			/// is created (so no attachmen_id either). On this request, the attachments record is created, so
			/// we need to add the id to the file object.
			file.attId = report.attachment_id;
			if (file.transcribe && !params.transcribe) {
				// I was hitting a race condition between confirming duplicate uploads, saying to transcribe,
				// creating the attachment, and starting transcription. I'm trying to catch those with this call.
				this.Api.post(`/attachments/${file.attId}/transcribe`, {
					s3_name: file.s3Name,
				});
			}
			this.createUploadedPost(file);
		});
	}

	/*
	 * Cancel the upload and remove the post shell
	 * @param file Object  plupload file object with other attributes added
	 * 					   with some extra attributes
	 */
	cancelDuplicate(uploadedFileIdx, file) {
		// plupload methods, otherwise it seems you can't upload the same file
		file.cancel();
		file.destroy();
		// remove the file from the modal
		this.filesWithDuplicates.splice(uploadedFileIdx, 1);
		// close all Video Transcription modals
		this.modal.closeAll('Video Transcription');
		// delete the post shell
		this.posts.some((post, postIdx) => {
			if (post.id == file.post.id) {
				// remove the post shell for this file
				this.posts.splice(postIdx, 1);
				return true;
			}
		});
	}

	/**
	 *
	 * @param {Object} file  Information about the file. Must at least include `s3Name`.
	 * @param {Object} error  Error information from plupload.
	 * @param {String} message Text that can be displayed to the user.
	 */
	handleUploadErrors(file, error, message, header) {
		this.posts.some(post => {
			if (file.s3Name && file.s3Name == post.file.s3Name) {
				post.fileError = message;
				this.$timeout(() => {});
				return true;
			}
		});

		this.modal.alert({ heading: header, text: message });
	}

	handleSearchSubmit() {
		// this updates the URL, which automatically sends the search request
		this.search.term = this.searchTerm;
		this.$timeout(() => {});
	}

	toggleSort(sortType) {
		this.search.orderBy = sortType;
		window.sessionStorage.setItem('Search.default_sort', sortType);
		this.sortLabel = this.sortOptions[sortType];
		this.sortIsOpen = false;
	}

	/**
	 * Callback called when a user adds or removes search criteria.
	 * Various UI elements are updated based on whether a search has been performed or not.
	 * @param appliedFilters Array  The filters being used in the current search
	 */
	fineTuneFiltersUpdated(appliedFilters) {
		appliedFilters = ensure.array(appliedFilters);
		this.filtersApplied = appliedFilters.length > 0 || Object.keys(appliedFilters).length > 0;
		this.$timeout(() => {});
	}

	onClassificationsLoaded(classifications) {
		this.classifications = classifications;
	}

	/**
	 * Loads this.categories, this.tags, etc. for use by sh-filter-message
	 */
	loadFineTuneMessageData() {
		this.FineTunePosts.getCriteriaData(this.search).then(items => {
			// items is an object with properties categories, tags, users, domains, classifications
			_.extend(this, items);
			this.$timeout(() => {});
		});
	}

	exitDashboard() {
		if (this.layout === 'dashboard') {
			let last = this.lastPostLayout || this.getLastLayout();
			this.setLayout(last);
		}
	}

	openTrends() {
		this.modal.iframe('/hub#/analytics/reports/top-post-ai-tag?in_iframe=true', {
			width: 1230,
			heading: 'Trending',
		});
	}

	/**
	 * Each simple post on the page is sent here to determine some extra styling. Simple posts are stacked in columns,
	 * and the comp required the final post in a column not have border or space underneat.
	 * @param postIndex Number  the index of the current post in the array of posts
	 * @param postsInAColumn Number  the number of posts that are required in each column. Used to determine if the
	 *      current post will be the final post in a column.
	 * @returns {{paddingBottom: *, marginBottom: *, borderBottom: string, height: string}}
	 */
	simplePostStyle(postIndex, postsInAColumn) {
		const isBottomOfColumn = (postIndex + 1) % postsInAColumn === 0;
		return {
			borderBottom: isBottomOfColumn ? 'none' : '',
			marginBottom: isBottomOfColumn ? 0 : '',
			paddingBottom: isBottomOfColumn ? 0 : '',
			height: isBottomOfColumn ? '80px' : '',
		};
	}

	viewPostFullscreen(postId) {
		this.modal.state('hub.PostDetail', {
			heading: 'Post Details',
			params: { id: postId },
		});
	}

	fetchNextPage() {
		this.FineTunePosts.fetchNextPage().then(posts => {
			this.posts.push(...posts);
		});
	}

	fetchNextFavoritesPage() {
		this.FineTunePosts.fetchNextFavoritesPage().then(posts => {
			this.myFavoritesPosts.push(...posts);
		});
	}

	fetchFirstFavoritesPage() {
		this.FineTunePosts.fetchFavorites(this.search).then(posts => {
			this.myFavoritesPosts.push(...posts);
		});
	}

	/**
	 * Submits an ecosystem post to Sharpr and loads it in the hero section
	 * @param ecoPost
	 *  @property headline
	 *  @property body
	 *  @property image.url
	 *  @property source_link
	 *  @property source_author
	 *  @property source_date
	 *  @property external_ref
	 */
	async createPostFromEcosystem(ecoPost) {
		ecoPost.publish = true;
		ecoPost.image = ecoPost.image.url || '';
		ecoPost.submission_medium = 'news_feed';
		const newId = await this.Api.create('/posts', ecoPost);
		if (!newId) {
			this.modal.alert({ text: 'Error adding News Feed item' });
			return;
		}
		const post = await this.Api.get(`/v3/posts/${newId}`);
		if (post) {
			this.spliceInNewPost(post);
		}
	}

	/**
	 * Use properties from an ecosystem post to create an object with params to `POST` to `/v2/posts`
	 * @param ecoPost Object
	 *  @property content_headline
	 *  @property content_full_text
	 *  @property page_url
	 *  @property content_domain_name
	 *  @property image_url (optional)
	 *  @property image_natural_width (ignored if !image_url)
	 *  @property image_natural_height (ignored if !image_url)
	 *  @property source_author (optional)
	 *  @property source_date (optional)
	 *  @property external_ref
	 * @returns Object  the object will have keys which
	 */
	convertEcoPostToHubPost(ecoPost) {
		const missingImgUrl = this.Theme.img('missing.png');
		return {
			// these are used to display the post
			headline: ecoPost.content_headline,
			body: ecoPost.content_full_text,
			source_link: ecoPost.page_url,
			// this is just for displaying as the post text in News Feed posts
			content_domain_name: ecoPost.content_domain_name,
			image: {
				// if we use the missingImgUrl, height and width are ignored
				url: ecoPost.image_url || missingImgUrl,
				width: ecoPost.image_natural_width || '',
				height: ecoPost.image_natural_height || '',
			},
			// these are only used  if the post is saved
			source_author: ecoPost.source_author || '',
			source_date: ecoPost.source_date ? moment.unix(ecoPost.source_date).format() : '',
			external_ref: ecoPost.external_ref,
		};
	}

	/**
	 * Calls the API endpoint for ecosystem posts. Adds dynamic search params based on properties in this controller.
	 * `ecosystemSources` tags and domain are always be applied when searching. `searchTerm` should be used if they have searched.
	 *
	 * @param sources Array  an array of objects with the following properties. Ecosystem results are filtered by these criteria.
	 *   @property url String  a domain e.g., 'npr.org'
	 *   @property tags Array  e.g., ['Politics', 'Tech', 'Apple NOT Disney']
	 *   @property from_dt String  no posts created before this date will be retrieved
	 * @returns {Promise<Array>}
	 */
	async fetchEcosystemPosts(sources) {
		let ecosystemUrl = this.Cfg.read('Ecosystem.gateway_address') + '/search';
		const page = this.ecosystemPostsPage;
		const sortBy = this.Cfg.read('Ecosystem.sort.sortBy');
		// decay options
		const functionType = this.Cfg.read('Ecosystem.sort.decay_function_type');
		const scale = this.Cfg.read('Ecosystem.sort.decay_scale');
		const offset = this.Cfg.read('Ecosystem.sort.decay_offset');
		const decayNumber = this.Cfg.read('Ecosystem.sort.decay_number');
		const sort = {
			sortBy,
			decayOptions: {
				decay: parseFloat(decayNumber),
				functionType,
				scale,
				offset,
			},
		};
		const params = { page, sources, sort };
		if (this.ecosystemJwt === undefined) {
			try {
				this.ecosystemJwt = await this.Api.get('/ecosystem/jwt');
			} catch (error) {
				console.log('error fetching ecosystem jwt', error);
				return Promise.reject('error fetching ecosystem jwt', error);
			}
		}

		// filter by search term if the user has searched on the home page
		if (this.search.term) {
			params.searchTerm = this.search.term;
		}
		try {
			const data = await this.Api.post(ecosystemUrl, params, {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				Authorization: `Bearer ${this.ecosystemJwt}`,
			});
			return data;
		} catch (error) {
			console.log('unable to fetch ecosystem posts', error);
			return false;
		}
	}

	/**
	 * Gets the posts and sets the variable to get them on the page
	 */
	async loadEcosystemPosts() {
		if (this.layout !== 'hero') {
			return;
		}
		// only run this on initial page load
		if (this.hasEcosystem === undefined) {
			const ecosystemSources = await this.Api.get('/ecosystem/client_sources');
			// creates an array of objects like this `{ domain: 'nytimes.com', minSourceDate: N, tags: ['Politics', 'Tech', 'Apple NOT Disney'] }`
			this.ecosystemSources = ensure.array(ecosystemSources).map(source => {
				const data = {
					domain: source.url,
					minSourceDate: new Date(source.from_dt).getTime() / 1000,
				};
				if (source.tags) {
					data.tags = source.tags;
				}
				return data;
			});
			this.hasEcosystem = ecosystemSources && ecosystemSources.length > 0;
		}
		// do not show News Feed section if ecosystem not set up in admin
		if (!this.hasEcosystem) {
			return;
		}
		this.ecosystemPostsPage++;
		try {
			const data = await this.fetchEcosystemPosts(this.ecosystemSources);
			if (!data || !data.pages || data.pages.length == 0) {
				this.ecosystemPosts = [];
				return;
			}
			const ecoPosts = data.pages.map(ecoPost => this.convertEcoPostToHubPost(ecoPost));
			if (this.ecosystemPostsPage == 1) {
				this.ecosystemPosts = ecoPosts;
			} else {
				this.ecosystemPosts.push(...ecoPosts);
			}
			this.$timeout(() => {});
		} catch (error) {
			console.log('error fetching and processing ecosystem posts', error);
		}
	}

	/**
	 * News Feed posts are filtered when the user enters a search term.
	 * This watches for changes in the search term and reruns the News Feed
	 * search if necessary.
	 */
	runEcosystemOnSearch() {
		this.$scope.$watch('vm.search.term', (newVal, oldVal) => {
			// the `(!newVal && !oldVal)` part is b/c sometimes values aren't equivalent,
			/// but are both falsey. E.g., `undefined` and `''`. Don't want to run
			/// if both are falsey. This can happen on initial page load.
			if (newVal == oldVal || (!newVal && !oldVal)) {
				return null;
			}
			// tells the slider it will need to expect some new contents

			// ran into a weird bonk on prod where posts was a string, setting length would error.
			// This ensures that doesn't happen
			if (Array.isArray(this.posts)) {
				this.posts.length = 0;
			} else {
				this.posts = [];
			}
			this.ecosystemSlideReload += 1;
			this.ecosystemPostsPage = 0;
			this.loadEcosystemPosts();
		});
	}

	/**
	 * Reads hub setting to determine which layout icons to show
	 */
	configureEnabledLayouts() {
		const layouts = this.Cfg.read('Ui.intel_layouts_enabled');
		if (layouts) {
			this.enabledLayouts = layouts
				.split(',')
				.map(layout => layout.trim())
				.reduce((enabled, layout) => {
					if (layout === 'list') {
						layout = 'tabular';
					}
					enabled[layout] = true;

					return enabled;
				}, {});
		} else {
			this.enabledLayouts = { hero: true, grid: true, pin: true, tabular: true };
		}
	}

	/**
	 * Determines which layout to go to when clicking view all on favorites. If a client only had the hero
	 * layout enabled they would stay on the hero layout.
	 */
	viewAllFavorites() {
		//layout needs to be set first otherwise it only sometimes applies the my favorites search
		this.layout = 'grid';
		this.$timeout(() => {
			this.search.myFavorites = true;
			this.search.multihub = 'mine';
		}, 50);
	}
}
